import { useDispatch, useSelector } from 'react-redux';
import {
  clearSelectedProduct,
  createProductAsync,
  fetchProductByIdAsync,
  selectCategories,
  selectProductById,
  updateProductAsync,
} from '../../product/productSlice';
import { useForm, useFieldArray } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import Modal from '../../common/Modal';
import { useAlert } from 'react-alert';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { uploadProductThumbnail } from '../../product/productAPI';

function ProductForm() {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    watch,
  } = useForm({
    defaultValues: {
      titleEn: '',
      titleFr: '',
      descriptionEn: '',
      descriptionFr: '',
      highlightsEn: [{ value: '' }],
      highlightsFr: [{ value: '' }],
      reviews: [{ customerName: '', reviewText: '', reviewTextFr: '', reviewRating: 5 }],
      commentsEn: '',
      commentsFr: '',
    },
  });

  const originalPrice = watch('originalPrice');
  const discountedPrice = watch('discountedPrice');
  const yearlyOriginalPrice = watch('yearlyOriginalPrice');
  const yearlyDiscountedPrice = watch('yearlyDiscountedPrice');

  // Calculate monthly discount percentage
  useEffect(() => {
    if (originalPrice && discountedPrice) {
      const discountPercent = Math.round(((originalPrice - discountedPrice) / originalPrice) * 100);
      if (discountPercent >= 0 && discountPercent <= 100) {
        setValue('discountPercentage', discountPercent);
      }
    }
  }, [originalPrice, discountedPrice, setValue]);

  // Calculate yearly discount percentage
  useEffect(() => {
    if (yearlyOriginalPrice && yearlyDiscountedPrice) {
      const discountPercent = Math.round(((yearlyOriginalPrice - yearlyDiscountedPrice) / yearlyOriginalPrice) * 100);
      if (discountPercent >= 0 && discountPercent <= 100) {
        setValue('yearlyDiscountPercentage', discountPercent);
      }
    }
  }, [yearlyOriginalPrice, yearlyDiscountedPrice, setValue]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'highlightsEn',
  });
  const { fields: highlightsFrFields, append: appendHighlightsFr, remove: removeHighlightsFr } = useFieldArray({
    control,
    name: 'highlightsFr',
  });
  const { fields: reviewFields, append: appendReview, remove: removeReview } = useFieldArray({
    control,
    name: 'reviews',
  });
  const categories = useSelector(selectCategories);
  const dispatch = useDispatch();
  const params = useParams();
  const selectedProduct = useSelector(selectProductById);
  const [openModal, setOpenModal] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const alert = useAlert();
  const loadedProductRef = useRef(null);

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert.error('Image file size must be under 5MB');
        return;
      }
      setUploading(true);
      try {
        const res = await uploadProductThumbnail(file);
        if (res.data?.url) {
          setThumbnailPreview(res.data.url);
          setValue('thumbnail', res.data.url);
          alert.success('Image uploaded to Hostinger successfully!');
        } else {
          alert.error('Failed to get uploaded image URL');
        }
      } catch (error) {
        console.error('Error uploading image to Hostinger:', error);
        alert.error(typeof error === 'string' ? error : 'Failed to upload image to Hostinger');
      } finally {
        setUploading(false);
      }
    }
  };

  useEffect(() => {
    // Clear the previous product first, then fetch the new one
    dispatch(clearSelectedProduct());
    loadedProductRef.current = null;
    
    if (params.id) {
      dispatch(fetchProductByIdAsync(params.id));
    } else {
      reset({
        titleEn: '',
        titleFr: '',
        descriptionEn: '',
        descriptionFr: '',
        highlightsEn: [{ value: '' }],
        highlightsFr: [{ value: '' }],
        reviews: [{ customerName: '', reviewText: '', reviewTextFr: '', reviewRating: 5 }],
        commentsEn: '',
        commentsFr: '',
      });
      setThumbnailPreview(null);
    }
  }, [params.id, dispatch, reset]);

  useEffect(() => {
    // Only populate form if selectedProduct matches the current params.id
    if (selectedProduct && params.id && selectedProduct.id?.toString() === params.id?.toString() && loadedProductRef.current !== params.id) {
      loadedProductRef.current = params.id;
      
      // Handle multilingual fields
      if (typeof selectedProduct.title === 'object') {
        setValue('titleEn', selectedProduct.title.en || '');
        setValue('titleFr', selectedProduct.title.fr || '');
      } else {
        setValue('titleEn', selectedProduct.title || '');
      }

      if (typeof selectedProduct.description === 'object') {
        setValue('descriptionEn', selectedProduct.description.en || '');
        setValue('descriptionFr', selectedProduct.description.fr || '');
      } else {
        setValue('descriptionEn', selectedProduct.description || '');
      }

      setValue('originalPrice', selectedProduct.price);
      setValue('discountedPrice', selectedProduct.discountPrice);
      setValue('discountPercentage', selectedProduct.discountPercentage);
      setValue('thumbnail', selectedProduct.thumbnail);
      setThumbnailPreview(selectedProduct.thumbnail);
      setValue('stock', selectedProduct.stock);
      setValue('category', selectedProduct.category);
      
      if (typeof selectedProduct.comments === 'object') {
        setValue('commentsEn', selectedProduct.comments.en || '');
        setValue('commentsFr', selectedProduct.comments.fr || '');
      } else {
        setValue('commentsEn', selectedProduct.comments || '');
      }
      
      setValue('rating', selectedProduct.rating || 0);
      setValue('yearlyOriginalPrice', selectedProduct.yearlyPrice);
      setValue('yearlyDiscountedPrice', selectedProduct.yearlyDiscountPrice);
      setValue('yearlyDiscountPercentage', selectedProduct.yearlyDiscountPercentage);
      
      if (selectedProduct.trustBadges) {
        setValue('isGenuine', selectedProduct.trustBadges.isGenuine ?? true);
        setValue('isSecure', selectedProduct.trustBadges.isSecure ?? true);
        setValue('moneyBackGuaranteed', selectedProduct.trustBadges.moneyBackGuaranteed ?? true);
      }

      // Prepare highlights data
      let highlightsEnArray = [];
      let highlightsFrArray = [];

      if (selectedProduct.highlights) {
        if (Array.isArray(selectedProduct.highlights)) {
          highlightsEnArray = selectedProduct.highlights;
        } else if (typeof selectedProduct.highlights === 'object') {
          highlightsEnArray = selectedProduct.highlights.en || [];
          highlightsFrArray = selectedProduct.highlights.fr || [];
        }
      }

      // Clear and rebuild English highlights
      const currentEnCount = fields.length;
      for (let i = currentEnCount - 1; i >= 0; i--) {
        remove(i);
      }
      highlightsEnArray.forEach((highlight) => {
        append({ value: highlight });
      });

      // Clear and rebuild French highlights
      const currentFrCount = highlightsFrFields.length;
      for (let i = currentFrCount - 1; i >= 0; i--) {
        removeHighlightsFr(i);
      }
      highlightsFrArray.forEach((highlight) => {
        appendHighlightsFr({ value: highlight });
      });

      // Handle reviews
      if (selectedProduct.reviews && selectedProduct.reviews.length > 0) {
        // First, remove all reviews except the first one
        while (reviewFields.length > 1) {
          removeReview(reviewFields.length - 1);
        }
        
        // Set the reviews
        selectedProduct.reviews.forEach((review, index) => {
          // Handle multilingual reviewText
          let reviewTextEn = review.reviewText;
          let reviewTextFr = '';
          
          if (typeof review.reviewText === 'object') {
            // If it's multilingual, extract both versions
            reviewTextEn = review.reviewText.en || '';
            reviewTextFr = review.reviewText.fr || '';
          }

          if (index === 0) {
            setValue(`reviews.${index}.customerName`, review.customerName);
            setValue(`reviews.${index}.reviewText`, reviewTextEn);
            setValue(`reviews.${index}.reviewTextFr`, reviewTextFr);
            setValue(`reviews.${index}.reviewRating`, review.reviewRating);
          } else {
            appendReview({ 
              customerName: review.customerName, 
              reviewText: reviewTextEn,
              reviewTextFr: reviewTextFr,
              reviewRating: review.reviewRating 
            });
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id, selectedProduct, dispatch]);

  const handleDelete = () => {
    const product = { ...selectedProduct };
    product.deleted = true;
    dispatch(updateProductAsync(product));
  };

  return (
    <>
      <form
        noValidate
        onSubmit={handleSubmit((data) => {
          // Validate that at least one pricing option is provided
          const hasMonthlyPrice = data.originalPrice && data.discountedPrice;
          const hasYearlyPrice = data.yearlyOriginalPrice && data.yearlyDiscountedPrice;
          
          if (!hasMonthlyPrice && !hasYearlyPrice) {
            alert.error('Please provide either monthly or yearly pricing');
            return;
          }

          // Validate multilingual fields
          if (!data.titleEn && !data.titleFr) {
            alert.error('Please provide product name in at least one language');
            return;
          }

          if (!data.descriptionEn && !data.descriptionFr) {
            alert.error('Please provide description in at least one language');
            return;
          }

          const product = { ...data };
          
          // Create multilingual title
          product.title = {
            en: data.titleEn || '',
            fr: data.titleFr || '',
          };

          // Create multilingual description
          product.description = {
            en: data.descriptionEn || '',
            fr: data.descriptionFr || '',
          };

          // Create multilingual highlights
          const highlightsEn = data.highlightsEn
            .map((h) => h.value)
            .filter((h) => h.trim() !== '');
          const highlightsFr = data.highlightsFr
            .map((h) => h.value)
            .filter((h) => h.trim() !== '');
          
          product.highlights = {
            en: highlightsEn,
            fr: highlightsFr,
          };

          // Create multilingual comments
          product.comments = {
            en: data.commentsEn || '',
            fr: data.commentsFr || '',
          };

          product.reviews = data.reviews
            .filter((r) => r.customerName && r.reviewText)
            .map((r) => ({
              customerName: r.customerName,
              reviewText: {
                en: r.reviewText || '',
                fr: r.reviewTextFr || '',
              },
              reviewRating: +r.reviewRating
            }));
          
          product.rating = +data.rating;
          product.trustBadges = {
            isGenuine: data.isGenuine || false,
            isSecure: data.isSecure || false,
            moneyBackGuaranteed: data.moneyBackGuaranteed || false
          };

          // Set monthly pricing (optional)
          product.price = hasMonthlyPrice ? +product.originalPrice : null;
          product.discountPrice = hasMonthlyPrice ? +product.discountedPrice : null;
          product.stock = +product.stock;
          product.discountPercentage = hasMonthlyPrice ? +product.discountPercentage : null;
          
          // Add yearly pricing (optional)
          product.yearlyPrice = hasYearlyPrice ? +product.yearlyOriginalPrice : null;
          product.yearlyDiscountPrice = hasYearlyPrice ? +product.yearlyDiscountedPrice : null;
          product.yearlyDiscountPercentage = hasYearlyPrice ? +product.yearlyDiscountPercentage : null;
          
          // Clean up temporary fields
          delete product.originalPrice;
          delete product.discountedPrice;
          delete product.yearlyOriginalPrice;
          delete product.yearlyDiscountedPrice;
          delete product.titleEn;
          delete product.titleFr;
          delete product.descriptionEn;
          delete product.descriptionFr;
          delete product.highlightsEn;
          delete product.highlightsFr;
          delete product.commentsEn;
          delete product.commentsFr;

          // Ensure thumbnail is set - use thumbnailPreview if form value is empty
          if (!product.thumbnail && thumbnailPreview) {
            product.thumbnail = thumbnailPreview;
          }
          // If still no thumbnail and editing, preserve the old one
          if (params.id && !product.thumbnail && selectedProduct?.thumbnail) {
            product.thumbnail = selectedProduct.thumbnail;
          }

          if (params.id) {
            product.id = params.id;
            dispatch(updateProductAsync(product));
            alert.success('Product Updated');
            reset();
          } else {
            dispatch(createProductAsync(product));
            alert.success('Product Created');
            reset();
          }
        })}
        className="min-h-screen w-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 overflow-x-hidden"
      >
        <div className="w-screen sm:max-w-5xl sm:mx-auto px-0 sm:px-3 md:px-4 lg:px-6 py-2 sm:py-4 md:py-6 lg:py-8">
          {/* Header */}
          <div className="mb-4 sm:mb-8 md:mb-12 px-3 sm:px-0">
            <h1 className="text-xl sm:text-3xl md:text-4xl font-bold text-slate-900 break-words">
              {params.id ? 'Edit Product' : 'Create Product'}
            </h1>
            <p className="mt-1 sm:mt-2 text-slate-600 text-xs sm:text-base md:text-lg break-words">
              {params.id ? 'Update product details and pricing' : 'Add a new product to your catalog'}
            </p>
          </div>

          {/* Error Alert */}
          {selectedProduct && selectedProduct.deleted && (
            <div className="mb-4 sm:mb-8 mx-3 sm:mx-0 p-2 sm:p-4 bg-red-50 border border-red-200 rounded-lg sm:rounded-xl flex items-start gap-2 sm:gap-3">
              <div className="text-red-600 mt-0.5 text-base sm:text-lg flex-shrink-0">⚠️</div>
              <div className="min-w-0">
                <h3 className="font-semibold text-red-900 text-xs sm:text-base break-words">Deleted Product</h3>
                <p className="text-xs text-red-700 mt-1">This product has been marked as deleted.</p>
              </div>
            </div>
          )}

          {/* Main Form Sections */}
          <div className="space-y-3 sm:space-y-6 px-3 sm:px-0">
            {/* Basic Information */}
            <FormSection title="Basic Information" number="1">
              <div className="grid grid-cols-1 gap-4 sm:gap-6">
                {/* English Title */}
                <div>
                  <FormInput
                    label="Product Name (English)"
                    id="titleEn"
                    register={register}
                    validation={{ required: 'Product name in English is required' }}
                    placeholder="e.g., Premium Wireless Headphones"
                  />
                </div>

                {/* French Title */}
                <div>
                  <FormInput
                    label="Product Name (French)"
                    id="titleFr"
                    register={register}
                    placeholder="e.g., Écouteurs sans fil premium"
                  />
                </div>

                {/* English Description */}
                <div>
                  <FormTextarea
                    label="Description (English)"
                    id="descriptionEn"
                    register={register}
                    validation={{ required: 'Description in English is required' }}
                    placeholder="Describe your product features and benefits..."
                    rows={4}
                  />
                </div>

                {/* French Description */}
                <div>
                  <FormTextarea
                    label="Description (French)"
                    id="descriptionFr"
                    register={register}
                    placeholder="Décrivez les caractéristiques et avantages de votre produit..."
                    rows={4}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <FormSelect
                      label="Category"
                      id="category"
                      register={register}
                      validation={{ required: 'Category is required' }}
                      options={categories}
                    />
                  </div>

                  <div>
                    <FormInput
                      label="Stock Quantity"
                      id="stock"
                      type="number"
                      register={register}
                      validation={{ required: 'Stock is required', min: 0 }}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </FormSection>

            {/* Pricing */}
            <FormSection title="Pricing & Discounts" number="2">
              <div className="mb-4 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs sm:text-sm text-blue-800">
                  💡 <strong>Tip:</strong> You can provide either monthly pricing, yearly pricing, or both. At least one pricing option is required.
                </p>
              </div>

              {/* Monthly Pricing */}
              <div className="mb-6 sm:mb-8 pb-6 sm:pb-8 border-b border-slate-200">
                <h3 className="text-sm sm:text-base font-semibold text-slate-900 mb-3 sm:mb-4">Monthly Pricing (Optional)</h3>
                <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-3">
                  <FormInput
                    label="Original Price"
                    id="originalPrice"
                    type="number"
                    register={register}
                    validation={{ min: 1 }}
                    placeholder="€0.00"
                  />

                  <FormInput
                    label="Discounted Price"
                    id="discountedPrice"
                    type="number"
                    register={register}
                    validation={{ min: 0 }}
                    placeholder="€0.00"
                  />

                  <FormInput
                    label="Discount %"
                    id="discountPercentage"
                    type="number"
                    register={register}
                    disabled={true}
                    placeholder="Auto-calculated"
                    hint="Automatically calculated from prices"
                  />
                </div>
              </div>

              {/* Yearly Pricing */}
              <div>
                <h3 className="text-base font-semibold text-slate-900 mb-4">Yearly Pricing (Optional)</h3>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                  <FormInput
                    label="Original Price"
                    id="yearlyOriginalPrice"
                    type="number"
                    register={register}
                    validation={{ min: 1 }}
                    placeholder="€0.00"
                  />

                  <FormInput
                    label="Discounted Price"
                    id="yearlyDiscountedPrice"
                    type="number"
                    register={register}
                    validation={{ min: 0 }}
                    placeholder="€0.00"
                  />

                  <FormInput
                    label="Discount %"
                    id="yearlyDiscountPercentage"
                    type="number"
                    register={register}
                    disabled={true}
                    placeholder="Auto-calculated"
                    hint="Automatically calculated from prices"
                  />
                </div>
              </div>
            </FormSection>

            {/* Media */}
            <FormSection title="Product Image" number="3">
              <div className="space-y-6">
                <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 hover:border-brand-400 transition-colors">
                  <div className="flex flex-col items-center justify-center gap-4">
                    {thumbnailPreview && (
                      <img src={thumbnailPreview} alt="Preview" className="w-32 h-32 object-cover rounded-lg" />
                    )}
                    <div className="text-center">
                      <p className="text-slate-700 font-medium">Upload or drag image here</p>
                      <p className="text-slate-500 text-sm mt-1">PNG, JPG, WebP up to 5MB</p>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailUpload}
                      disabled={uploading}
                      className="block w-full text-sm text-slate-500
                        file:mr-4 file:py-2.5 file:px-4
                        file:rounded-lg file:border-0
                        file:text-sm file:font-semibold
                        file:bg-brand-50 file:text-brand-700
                        hover:file:bg-brand-100
                        cursor-pointer"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Or paste image URL
                  </label>
                  <input
                    type="text"
                    {...register('thumbnail', { required: 'Image is required' })}
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>
            </FormSection>

            {/* Highlights */}
            <FormSection title="Key Features" number="4">
              <div className="space-y-8">
                {/* English Highlights */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-4">English Features</h3>
                  <div className="space-y-3">
                    {fields.map((field, index) => (
                      <div key={field.id} className="flex gap-3">
                        <input
                          type="text"
                          {...register(`highlightsEn.${index}.value`)}
                          placeholder={`Feature ${index + 1}`}
                          className="flex-1 px-4 py-2.5 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                        />
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => append({ value: '' })}
                      className="w-full py-2.5 px-4 border border-dashed border-slate-300 rounded-lg text-brand-600 hover:bg-brand-50 font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Add Feature
                    </button>
                  </div>
                </div>

                {/* French Highlights */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-4">French Features</h3>
                  <div className="space-y-3">
                    {highlightsFrFields.map((field, index) => (
                      <div key={field.id} className="flex gap-3">
                        <input
                          type="text"
                          {...register(`highlightsFr.${index}.value`)}
                          placeholder={`Fonctionnalité ${index + 1}`}
                          className="flex-1 px-4 py-2.5 bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                        />
                        <button
                          type="button"
                          onClick={() => removeHighlightsFr(index)}
                          className="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => appendHighlightsFr({ value: '' })}
                      className="w-full py-2.5 px-4 border border-dashed border-slate-300 rounded-lg text-brand-600 hover:bg-brand-50 font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Add Feature
                    </button>
                  </div>
                </div>
              </div>
            </FormSection>

            {/* Rating & Admin Notes */}
            <FormSection title="Additional Info" number="5">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <FormInput
                  label="Rating (0-5)"
                  id="rating"
                  type="number"
                  register={register}
                  validation={{ min: 0, max: 5 }}
                  placeholder="4.5"
                  step="0.1"
                />

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Trust Badges
                  </label>
                  <div className="space-y-2">
                    <FormCheckbox
                      id="isGenuine"
                      label="✅ 100% Genuine & Original"
                      register={register}
                    />
                    <FormCheckbox
                      id="isSecure"
                      label="🛡️ Secure & Safe"
                      register={register}
                    />
                    <FormCheckbox
                      id="moneyBackGuaranteed"
                      label="💯 Money Back Guaranteed"
                      register={register}
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <FormTextarea
                    label="Admin Comments (English)"
                    id="commentsEn"
                    register={register}
                    placeholder="Internal notes about this product..."
                    rows={3}
                  />
                </div>

                <div className="sm:col-span-2">
                  <FormTextarea
                    label="Admin Comments (French)"
                    id="commentsFr"
                    register={register}
                    placeholder="Notes internes sur ce produit..."
                    rows={3}
                  />
                </div>
              </div>
            </FormSection>

            {/* Customer Reviews */}
            <FormSection title="Customer Reviews" number="6">
              <div className="space-y-4">
                {reviewFields.map((field, index) => (
                  <div key={field.id} className="bg-slate-50 border border-slate-200 rounded-lg p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="font-medium text-slate-900">Review {index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => removeReview(index)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mb-4">
                      <FormInput
                        label="Customer Name"
                        id={`reviews.${index}.customerName`}
                        register={register}
                        placeholder="John Doe"
                        nested={true}
                      />
                      <FormInput
                        label="Rating"
                        id={`reviews.${index}.reviewRating`}
                        type="number"
                        register={register}
                        validation={{ min: 0, max: 5 }}
                        placeholder="5"
                        step="0.5"
                        nested={true}
                      />
                    </div>

                    <FormTextarea
                      label="Review Text (English)"
                      id={`reviews.${index}.reviewText`}
                      register={register}
                      placeholder="Customer review in English..."
                      rows={3}
                      nested={true}
                    />

                    <FormTextarea
                      label="Review Text (French)"
                      id={`reviews.${index}.reviewTextFr`}
                      register={register}
                      placeholder="Avis du client en français..."
                      rows={3}
                      nested={true}
                    />
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => appendReview({ customerName: '', reviewText: '', reviewTextFr: '', reviewRating: 5 })}
                  className="w-full py-2.5 px-4 border border-dashed border-slate-300 rounded-lg text-brand-600 hover:bg-brand-50 font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add Review
                </button>
              </div>
            </FormSection>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-12 pt-8 border-t border-slate-200">
            <button
              type="button"
              className="px-6 py-2.5 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 font-medium transition-colors"
            >
              Cancel
            </button>

            {selectedProduct && !selectedProduct.deleted && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setOpenModal(true);
                }}
                className="px-6 py-2.5 bg-red-50 border border-red-200 text-red-600 rounded-lg hover:bg-red-100 font-medium transition-colors"
              >
                Delete
              </button>
            )}

            <button
              type="submit"
              className="ml-auto px-6 py-2.5 bg-brand-600 text-white rounded-lg hover:bg-brand-700 font-medium shadow-lg hover:shadow-xl transition-all"
            >
              {params.id ? 'Update Product' : 'Create Product'}
            </button>
          </div>
        </div>
      </form>

      {selectedProduct && (
        <Modal
          title={`Delete ${selectedProduct.title}`}
          message="Are you sure you want to delete this product?"
          dangerOption="Delete"
          cancelOption="Cancel"
          dangerAction={handleDelete}
          cancelAction={() => setOpenModal(null)}
          showModal={openModal}
        />
      )}
    </>
  );
}

// Component Helpers
function FormSection({ title, number, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg sm:rounded-xl p-3 sm:p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow mx-0 sm:mx-0">
      <h2 className="text-sm sm:text-lg font-semibold text-slate-900 mb-3 sm:mb-6 flex items-center gap-2 sm:gap-3">
        <span className="flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-brand-100 text-brand-600 text-xs sm:text-sm font-bold flex-shrink-0">
          {number}
        </span>
        <span className="truncate">{title}</span>
      </h2>
      <div>{children}</div>
    </div>
  );
}

function FormInput({
  label,
  id,
  type = 'text',
  register,
  validation,
  placeholder,
  disabled = false,
  hint,
  step,
  nested = false,
}) {
  const registerName = nested ? id : id;
  return (
    <div>
      {label && (
        <label htmlFor={id} className="block text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
          {label}
        </label>
      )}
      <input
        type={type}
        {...register(registerName, validation)}
        id={id}
        placeholder={placeholder}
        disabled={disabled}
        step={step}
        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all ${
          disabled
            ? 'bg-slate-100 border-slate-300 text-slate-500 cursor-not-allowed'
            : 'bg-white border-slate-300'
        }`}
      />
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

function FormTextarea({
  label,
  id,
  register,
  validation,
  placeholder,
  rows = 3,
  nested = false,
}) {
  const registerName = nested ? id : id;
  return (
    <div>
      {label && (
        <label htmlFor={id} className="block text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
          {label}
        </label>
      )}
      <textarea
        {...register(registerName, validation)}
        id={id}
        placeholder={placeholder}
        rows={rows}
        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all resize-none"
      />
    </div>
  );
}

function FormSelect({ label, id, register, validation, options }) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
        {label}
      </label>
      <select
        {...register(id, validation)}
        id={id}
        className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
      >
        <option value="">-- Select {label} --</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function FormCheckbox({ id, label, register }) {
  return (
    <div className="flex items-center gap-3">
      <input
        id={id}
        type="checkbox"
        {...register(id)}
        className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 accent-brand-600 cursor-pointer"
      />
      <label htmlFor={id} className="text-sm text-slate-700 cursor-pointer font-medium">
        {label}
      </label>
    </div>
  );
}

export default ProductForm;
